<script setup lang="ts">
import { computed } from "vue";
import { chartTheme, statusColor, statusSoftColor, type ChartStatus } from "./theme";

const props = withDefaults(
  defineProps<{
    percent: number;
    status?: ChartStatus;
    size?: number;
    strokeWidth?: number;
    /** Track in the soft shade of the status color instead of neutral grey. */
    tintedTrack?: boolean;
  }>(),
  { status: "success", size: 56, strokeWidth: 4, tintedTrack: false }
);

const radius = computed(() => (props.size - props.strokeWidth) / 2);
const circumference = computed(() => 2 * Math.PI * radius.value);
const clampedPercent = computed(() => Math.min(100, Math.max(0, props.percent)));
const dashOffset = computed(() => circumference.value * (1 - clampedPercent.value / 100));
const trackColor = computed(() => (props.tintedTrack ? statusSoftColor(props.status) : chartTheme.color.grid));
const rootStyle = computed(() => ({ width: `${props.size}px`, height: `${props.size}px` }));

const ui = {
  root: "relative inline-flex shrink-0 items-center justify-center",
  svg: "absolute inset-0 -rotate-90",
  fill: "transition-all duration-500",
  content: "relative flex flex-col items-center justify-center text-center",
};
</script>

<template>
  <div
    :class="ui.root"
    :style="rootStyle"
    role="progressbar"
    :aria-valuenow="clampedPercent"
    aria-valuemin="0"
    aria-valuemax="100"
  >
    <svg :class="ui.svg" :width="size" :height="size" :viewBox="`0 0 ${size} ${size}`">
      <circle :cx="size / 2" :cy="size / 2" :r="radius" fill="none" :stroke="trackColor" :stroke-width="strokeWidth" />
      <circle
        :class="ui.fill"
        :cx="size / 2"
        :cy="size / 2"
        :r="radius"
        fill="none"
        :stroke="statusColor(status)"
        :stroke-width="strokeWidth"
        stroke-linecap="round"
        :stroke-dasharray="circumference"
        :stroke-dashoffset="dashOffset"
      />
    </svg>
    <div :class="ui.content">
      <slot></slot>
    </div>
  </div>
</template>
